import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { reportingApi } from '../../../services/api';

// Measure text width using an offscreen canvas (cached per font)
const measureCanvas = document.createElement('canvas').getContext('2d');
const measureTextWidth = (text, font = '13px sans-serif') => {
  measureCanvas.font = font;
  return measureCanvas.measureText(text).width;
};

/**
 * Multi-select combobox for report parameters.
 * - Loads available options from API (FK display values or distinct raw values)
 * - Supports free-text entry with operator (contains, startsWith, endsWith, equals)
 * - Picked-from-list values auto-set to "equals"
 * - Multiple selections rendered as removable chips
 * - All selections OR'd together in the query
 * - Auto-sizes input width to longest option value
 */
const ReportParameterCombobox = ({ param, selections, onChange, reportId, activeFilters }) => {
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [operator, setOperator] = useState(param.defaultOperator || 'contains');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Build filters payload excluding this parameter's own selections
  const filtersKey = useMemo(() => {
    if (!activeFilters) return '';
    const relevant = {};
    Object.entries(activeFilters).forEach(([field, sels]) => {
      if (field === param.field) return; // skip own field
      if (!sels || sels.length === 0) return;
      const camelField = field.charAt(0).toLowerCase() + field.slice(1);
      relevant[camelField] = sels.map(s => ({ operator: s.operator, value: s.value }));
    });
    return JSON.stringify(relevant);
  }, [activeFilters, param.field]);

  // Load options on demand when the dropdown opens, or when filters change while already open
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const loadOptions = useCallback(() => {
    setOptionsLoading(true);
    const filterObj = filtersKey && filtersKey !== '{}' ? JSON.parse(filtersKey) : null;
    reportingApi.getParameterOptions(reportId, param.field, filterObj)
      .then(res => { setOptions(res.data || []); setOptionsLoaded(true); })
      .catch(() => setOptions([]))
      .finally(() => setOptionsLoading(false));
  }, [reportId, param.field, filtersKey]);

  // Reload when filters change and dropdown is already open
  useEffect(() => {
    if (isOpen && optionsLoaded) loadOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Calculate input width based on longest option + checkbox + padding
  const inputWidth = useMemo(() => {
    if (options.length === 0) return 180;
    const longestText = options.reduce((longest, opt) => {
      const display = String(opt.display || '');
      return display.length > longest.length ? display : longest;
    }, '');
    // Measure with the same font the dropdown uses (13px), add padding for checkbox + scrollbar
    const textWidth = measureTextWidth(longestText, '13px sans-serif');
    // checkbox(16) + gap(8) + text + padding(24) + scrollbar(16)
    const totalWidth = Math.ceil(textWidth) + 64;
    // Clamp between 180 and 500
    return Math.max(180, Math.min(totalWidth, 500));
  }, [options]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on input
  const filteredOptions = options.filter(opt => {
    const display = String(opt.display || '').toLowerCase();
    const search = inputValue.toLowerCase();
    if (!search) return true;
    return display.includes(search);
  });

  const MAX_SELECTIONS = 4;
  const [maxWarning, setMaxWarning] = useState(false);
  const atMax = selections.length >= MAX_SELECTIONS;

  // Check if an option is already selected
  const isSelected = useCallback((opt) => {
    return selections.some(s =>
      s.type === 'picked' && String(s.id) === String(opt.id)
    );
  }, [selections]);

  // Add a picked selection (from dropdown)
  const addPicked = (opt) => {
    if (isSelected(opt)) {
      // Remove is always allowed
      onChange(selections.filter(s => !(s.type === 'picked' && String(s.id) === String(opt.id))));
      setMaxWarning(false);
    } else {
      if (atMax) {
        setMaxWarning(true);
        return;
      }
      onChange([...selections, {
        type: 'picked',
        id: opt.id,
        display: opt.display,
        fullDisplay: opt.fullDisplay || null,
        operator: 'equals',
        value: String(opt.id)
      }]);
      setMaxWarning(false);
    }
    setInputValue('');
    inputRef.current?.focus();
  };

  // Add a free-text selection
  const addFreeText = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    if (atMax) {
      setMaxWarning(true);
      return;
    }

    // Don't add duplicate free-text entries
    const isDuplicate = selections.some(s =>
      s.type === 'freetext' && s.value === trimmed && s.operator === operator
    );
    if (isDuplicate) return;

    onChange([...selections, {
      type: 'freetext',
      id: null,
      display: `${operator}: "${trimmed}"`,
      operator: operator,
      value: trimmed
    }]);
    setInputValue('');
    setMaxWarning(false);
    inputRef.current?.focus();
  };

  // Remove a selection
  const removeSelection = (index) => {
    onChange(selections.filter((_, i) => i !== index));
    setMaxWarning(false);
  };

  // Clear all selections
  const clearAll = () => {
    onChange([]);
    setInputValue('');
    setMaxWarning(false);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < filteredOptions.length) {
        addPicked(filteredOptions[highlightIndex]);
        setHighlightIndex(-1);
      } else if (inputValue.trim()) {
        addFreeText();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) { setIsOpen(true); if (!optionsLoaded) loadOptions(); }
      setHighlightIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Backspace' && !inputValue && selections.length > 0) {
      removeSelection(selections.length - 1);
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightIndex >= 0 && dropdownRef.current) {
      const item = dropdownRef.current.children[highlightIndex];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex]);

  const operators = param.operators || ['contains', 'startsWith', 'endsWith', 'equals'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: `${inputWidth + 94}px` }}>
      <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>{param.label}</label>

      <div ref={containerRef} style={{ position: 'relative' }}>
        {/* Operator + Input row */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: selections.length > 0 ? '6px' : 0 }}>
          <select
            value={operator}
            onChange={(e) => setOperator(e.target.value)}
            style={{
              padding: '6px 6px', borderRadius: '4px', border: '1px solid #ccc',
              fontSize: '11px', background: '#fff', flexShrink: 0, width: '90px'
            }}
          >
            {operators.map(op => (
              <option key={op} value={op}>{op}</option>
            ))}
          </select>

          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center',
            flex: 1, border: '1px solid #ccc', borderRadius: '4px', padding: '3px 6px',
            background: '#fff', minHeight: '30px', cursor: 'text',
            outline: isOpen ? '2px solid #1f497d' : 'none'
          }}
            onClick={() => inputRef.current?.focus()}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setIsOpen(true);
                if (!optionsLoaded) loadOptions();
                setHighlightIndex(-1);
              }}
              onFocus={() => { setIsOpen(true); if (!optionsLoaded) loadOptions(); }}
              onKeyDown={handleKeyDown}
              placeholder={selections.length === 0 ? (optionsLoading ? 'Loading...' : `Type or select ${param.label}...`) : ''}
              style={{
                border: 'none', outline: 'none', fontSize: '13px', flex: 1,
                minWidth: '80px', padding: '2px 0', background: 'transparent'
              }}
            />
          </div>

          {selections.length > 0 && (
            <button
              onClick={clearAll}
              title="Clear all"
              style={{
                padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px',
                background: '#f5f5f5', cursor: 'pointer', fontSize: '12px', color: '#666',
                flexShrink: 0
              }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Max selections warning */}
        {maxWarning && (
          <div style={{
            padding: '6px 10px', marginBottom: '4px', borderRadius: '4px',
            background: '#fee2e2', border: '1px solid #fca5a5',
            fontSize: '11px', color: '#991b1b', lineHeight: 1.4
          }}>
            There is a maximum number of 4 values that can be selected for this parameter.
            Please choose alternative methods of viewing the desired data.
          </div>
        )}

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute', top: '100%', left: '94px',
              width: `${inputWidth}px`,
              maxHeight: '250px', overflowY: 'auto',
              background: '#fff', border: '1px solid #ccc', borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000,
              marginTop: '2px'
            }}
          >
            {/* Free text entry hint */}
            {inputValue.trim() && (
              <div
                onClick={addFreeText}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: '12px',
                  borderBottom: '1px solid #eee', background: '#fffbeb', color: '#856404'
                }}
                onMouseEnter={(e) => e.target.style.background = '#fef3cd'}
                onMouseLeave={(e) => e.target.style.background = '#fffbeb'}
              >
                Add as filter: <strong>{operator}</strong> "{inputValue.trim()}"
              </div>
            )}

            {optionsLoading ? (
              <div style={{ padding: '12px', color: '#999', fontSize: '12px', textAlign: 'center' }}>
                Loading options...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div style={{ padding: '12px', color: '#999', fontSize: '12px', textAlign: 'center' }}>
                {inputValue ? 'No matching options' : 'No options available'}
              </div>
            ) : (
              filteredOptions.slice(0, 100).map((opt, idx) => {
                const selected = isSelected(opt);
                const highlighted = idx === highlightIndex;
                return (
                  <div
                    key={opt.id}
                    onClick={() => addPicked(opt)}
                    style={{
                      padding: '6px 12px', cursor: 'pointer', fontSize: '13px',
                      display: 'flex', alignItems: 'center', gap: '8px',
                      background: highlighted ? '#e3edf7' : selected ? '#f0f7ff' : '#fff',
                      borderBottom: '1px solid #f5f5f5'
                    }}
                    onMouseEnter={(e) => {
                      setHighlightIndex(idx);
                      if (!selected) e.currentTarget.style.background = '#f0f4f8';
                    }}
                    onMouseLeave={(e) => {
                      if (!selected && !highlighted) e.currentTarget.style.background = '#fff';
                    }}
                  >
                    <span style={{
                      width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0,
                      border: selected ? '2px solid #1f497d' : '2px solid #ccc',
                      background: selected ? '#1f497d' : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '10px', color: '#fff'
                    }}>
                      {selected && '\u2713'}
                    </span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {opt.display}
                    </span>
                  </div>
                );
              })
            )}
            {filteredOptions.length > 100 && (
              <div style={{ padding: '8px 12px', color: '#999', fontSize: '11px', textAlign: 'center', borderTop: '1px solid #eee' }}>
                Showing first 100 of {filteredOptions.length} — type to narrow results
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportParameterCombobox;
